import { type UserProductWithDetails, parseImageAnalysis } from '../../types/newDatabaseTypes';
import type { PayloadAction } from '@reduxjs/toolkit';
import { itemValuationState } from '../../lib/itemValuation';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  getUserProducts, 
  getUserProductById, 
  addUserProduct, 
  updateUserProduct, 
  deleteUserProduct 
} from '../../utils/userProductStorage';

// Define a type for the data needed specifically by the Cache List
// Export this interface for use in components
// Define image object type to match the structure in the database
export interface ImageObject {
  storage_path: string;
  [key: string]: any; // Allow other properties
}

export interface CacheListItemData {
  id: string;
  user_id: string;
  variant_id: string | null;
  condition: string | null;
  valuation: number | null;
  valuation_source?: string | null;
  restoration_valuation?: boolean;
  restoration_pending?: boolean;
  restoration_market_qualified?: boolean;
  // V3 item state: render through src/lib/itemValuation.ts, never read valuation directly.
  valuation_status?: string | null;
  valuation_basis?: string | null;
  identity_status?: string | null;
  identity_reason?: string | null;
  currency: string | null;
  purchase_price: number | null;
  images: string[] | ImageObject[] | null;
  created_at: string;
  // Status and removal metadata
  // Support both new status values and legacy ones for backward compatibility
  status: 'owned' | 'tracking' | 'removed' | 'active' | 'archived' | 'sold';
  removed_at?: string | null;
  removal_price?: number | null;
  removal_reason?: 'sold' | 'lost' | 'given_away' | 'other' | null;
  removal_notes?: string | null;
  // Flattened variant details needed for display
  brandName?: string;
  modelName?: string;
  familyName?: string;
  variantAttributes?: Record<string, any>;
  // Confidence and analysis data
  confidenceScore?: number;
  imageAnalysis?: Record<string, any>;
  updatedAt?: string;
}

// Helper function to map full details to list item data
const mapToCacheListItemData = (product: UserProductWithDetails | null): CacheListItemData | null => {
  if (!product) return null;

  // Debug the nested structure
  console.log('DEBUG - Product ID:', product.id);
  console.log('DEBUG - Variant:', product.variant ? 'exists' : 'missing');
  if (product.variant) {
    console.log('DEBUG - Model:', product.variant.model ? 'exists' : 'missing');
    if (product.variant.model) {
      console.log('DEBUG - Family:', product.variant.model.family ? 'exists' : 'missing');
      if (product.variant.model.family) {
        console.log('DEBUG - Brand:', product.variant.model.family.brand ? 'exists' : 'missing');
        if (product.variant.model.family.brand) {
          console.log('DEBUG - Brand Name:', product.variant.model.family.brand.name);
        }
      }
      console.log('DEBUG - Model Name:', product.variant.model.name);
    }
  }

  // Handle images which could be an array of objects with storage_path
  let processedImages = null;
  if (Array.isArray(product.images)) {
    // Check if we have objects with storage_path or direct strings
    if (product.images.length > 0 && typeof product.images[0] === 'object') {
      // Check if it's an object with storage_path property
      const firstImg = product.images[0] as any;
      if (firstImg && firstImg.storage_path) {
        processedImages = product.images as unknown as ImageObject[]; // Cast to ImageObject array
      }
    } else {
      // Filter for string values only
      processedImages = product.images.filter((img): img is string => typeof img === 'string');
      if (processedImages.length === 0) processedImages = null;
    }
  }

  // Safely handle attributes type (Json | null -> Record<string, any> | undefined)
  const attributes = product.variant?.attributes && typeof product.variant.attributes === 'object' && !Array.isArray(product.variant.attributes)
    ? product.variant.attributes as Record<string, any>
    : undefined;

  // Extract confidence score and image analysis data
  const imageAnalysis = parseImageAnalysis(product.image_analysis);
  const confidenceScore = imageAnalysis.confidence_score ?? imageAnalysis.confidenceScore ?? undefined;

  return {
    id: product.id,
    user_id: product.user_id,
    variant_id: product.variant_id,
    condition: product.condition,
    valuation: product.valuation,
    valuation_source: product.valuation_source,
    restoration_valuation: product.restoration_valuation,
    restoration_pending: product.restoration_pending,
    restoration_market_qualified: product.restoration_market_qualified,
    valuation_status: product.valuation_status ?? null,
    valuation_basis: product.valuation_basis ?? null,
    identity_status: product.identity_status ?? null,
    identity_reason: product.identity_reason ?? null,
    currency: product.currency,
    purchase_price: product.purchase_price,
    images: processedImages, // Use the processed images
    created_at: product.created_at,
    status: product.status,
    removed_at: product.removed_at,
    removal_price: product.removal_price,
    removal_reason: product.removal_reason,
    removal_notes: product.removal_notes,
    brandName: product.variant?.model?.family?.brand?.name || imageAnalysis.brand,
    modelName: product.variant?.model?.name || imageAnalysis.model,
    familyName: product.variant?.model?.family?.name || imageAnalysis.family,
    variantAttributes: attributes, // Use the safely typed attributes
    confidenceScore: confidenceScore,
    imageAnalysis: imageAnalysis,
  };
};

// Product categorization types and utilities
// Note: 'duplicates' is for internal detection only, not a user-facing tab
export type ProductCategory = 'verified' | 'needs_review' | 'unknown' | 'duplicates';

export interface ProductCategorization {
  verified: CacheListItemData[];
  needs_review: CacheListItemData[];
  unknown: CacheListItemData[];
  duplicates: CacheListItemData[]; // Items that are part of duplicate groups
}

// Constants for categorization
const UNKNOWN_BRAND_ID = 'a984a92a-ccea-490d-961d-b8792de1b640';
const HIGH_CONFIDENCE_THRESHOLD = 0.9;
const LOW_CONFIDENCE_THRESHOLD = 0.3;

// Helper function to categorize a single product
// Matches the snapshot filtering logic from create_portfolio_snapshot
export const categorizeProduct = (product: CacheListItemData): ProductCategory => {
  const confidence = product.confidenceScore;
  
  // Check if this is an unknown brand
  const isUnknownBrand = !product.brandName || 
                        product.brandName.toLowerCase() === 'unknown';
  
  // Items needing review (matches snapshot exclusion criteria):
  // - Unknown brand
  // - Missing model name
  // - Missing valuation (legacy item) or unresolved identity (V3 item)
  // - Confidence < 0.3 (if confidence is present)
  // Note: NULL confidence is OK (manually verified items)
  
  if (isUnknownBrand) {
    return 'needs_review';
  }
  
  // V3 P6: a legacy item with no value still needs review (unchanged); a V3 item needs
  // review when its identity is unresolved. A V3 item that is only awaiting its projected
  // valuation does not.
  const valuationState = itemValuationState(product);
  if (!product.modelName || valuationState === 'unvalued' || valuationState === 'identity_unresolved') {
    return 'needs_review';
  }
  
  if (confidence != null && confidence < LOW_CONFIDENCE_THRESHOLD) {
    return 'needs_review';
  }
  
  // All other items are verified
  return 'verified';
};

// Helper function to get items that are part of duplicate groups
export const getDuplicateItems = (products: CacheListItemData[]): CacheListItemData[] => {
  // Import is at top of file, use it here
  const { detectInventoryDuplicates } = require('../../utils/inventoryDuplicateDetection');
  const duplicateGroups = detectInventoryDuplicates(products);
  
  // Collect all item IDs that are in duplicate groups
  const duplicateItemIds = new Set<string>();
  duplicateGroups.forEach((group: any) => {
    group.itemIds.forEach((id: string) => duplicateItemIds.add(id));
  });
  
  // Return items that are in duplicate groups
  return products.filter(item => duplicateItemIds.has(item.id));
};

// Helper function to categorize all products
export const categorizeProducts = (products: CacheListItemData[]): ProductCategorization => {
  const categorization: ProductCategorization = {
    verified: [],
    needs_review: [],
    unknown: [],
    duplicates: []
  };
  
  // Get duplicate items
  categorization.duplicates = getDuplicateItems(products);
  
  products.forEach(product => {
    const category = categorizeProduct(product);
    // Don't add to duplicates here - it's handled separately
    if (category !== 'duplicates') {
      categorization[category].push(product);
    }
  });
  
  return categorization;
};

// Helper functions to filter products by status
export const getOwnedProducts = (products: CacheListItemData[]): CacheListItemData[] => {
  return products.filter(p => p.status === 'owned');
};

export const getTrackingProducts = (products: CacheListItemData[]): CacheListItemData[] => {
  return products.filter(p => p.status === 'tracking');
};

export const getRemovedProducts = (products: CacheListItemData[]): CacheListItemData[] => {
  return products.filter(p => p.status === 'removed');
};

// Combined categorization by status and quality
export interface StatusCategorization {
  owned: {
    verified: CacheListItemData[];
    needs_review: CacheListItemData[];
  };
  tracking: CacheListItemData[];
  removed: CacheListItemData[];
}

export const categorizeProductsByStatus = (products: CacheListItemData[]): StatusCategorization => {
  const owned = getOwnedProducts(products);
  const tracking = getTrackingProducts(products);
  const removed = getRemovedProducts(products);
  
  // Further categorize owned items by quality
  const ownedCategorization = categorizeProducts(owned);
  
  return {
    owned: {
      verified: ownedCategorization.verified,
      needs_review: ownedCategorization.needs_review,
    },
    tracking,
    removed,
  };
};

export interface UserProductsState {
  items: CacheListItemData[]; 
  loading: 'idle' | 'pending' | 'succeeded' | 'failed';
  error: string | null;
  selectedItem: CacheListItemData | null; 
  // The user the last settled fetchUserProducts ran for, whatever rows it returned.
  fetchedForUserId: string | null;
}

const initialState: UserProductsState = {
  items: [],
  loading: 'idle',
  error: null,
  selectedItem: null,
  fetchedForUserId: null,
};

// An old account's in-flight fetch makes the thunk condition drop the new account's fetch;
// once that request settles (any rows, or failed), the new account still needs its own.
export const needsAccountRefetch = (state: UserProductsState, userId: string): boolean =>
  state.loading !== 'pending' && state.loading !== 'idle' && state.fetchedForUserId !== userId;

export const fetchUserProducts = createAsyncThunk(
  'userProducts/fetchAll',
  async (userId: string, { rejectWithValue }) => {
    console.log('fetchUserProducts thunk called with userId:', userId);
    try {
      const products: UserProductWithDetails[] = await getUserProducts(userId);
      console.log('getUserProducts returned:', products ? `${products.length} products` : 'no products');
      if (products && products.length > 0) {
        console.log('Sample product (full):', JSON.stringify(products[0], null, 2));
      }
      // Map to the simplified type for the store
      const mappedProducts = products.map(mapToCacheListItemData).filter(p => p !== null) as CacheListItemData[];
      console.log('Sample product (mapped):', JSON.stringify(mappedProducts[0], null, 2));
      return mappedProducts;
    } catch (error) {
      console.error('Error in fetchUserProducts thunk:', error);
      return rejectWithValue('Failed to fetch user products: ' + (error as Error).message);
    }
  },
  { condition: (_, { getState }) => (getState() as { userProducts: UserProductsState }).userProducts.loading !== 'pending' }
);

export const fetchUserProductById = createAsyncThunk(
  'userProducts/fetchById',
  async (id: string) => {
    const product = await getUserProductById(id);
    if (!product) {
      throw new Error(`User product with ID ${id} not found`);
    }
    // Map to the simplified type
    const mappedProduct = mapToCacheListItemData(product);
    if (!mappedProduct) {
        throw new Error(`Failed to map product ${id}`);
    }
    return mappedProduct;
  }
);

export const addNewUserProduct = createAsyncThunk(
  'userProducts/add',
  async (product: any) => {
    const result = await addUserProduct(product);
    // Fetch the full details after adding, then map
    if (result?.id) {
        const fullProduct = await getUserProductById(result.id);
        const mappedProduct = mapToCacheListItemData(fullProduct);
        if (mappedProduct) return mappedProduct;
    }
    throw new Error('Failed to add or retrieve new product details');
  }
);

export const updateExistingUserProduct = createAsyncThunk(
  'userProducts/update',
  async ({ id, updates }: { id: string, updates: any }) => {
    const result = await updateUserProduct(id, updates);
    // The updateUserProduct function returns UserProductWithDetails
    // Map to the simplified type
    const mappedResult = mapToCacheListItemData(result);
    if (!mappedResult) {
        throw new Error(`Failed to update or retrieve product ${id}`);
    }
    return mappedResult;
  }
);

export const removeUserProduct = createAsyncThunk(
  'userProducts/delete',
  async (id: string) => {
    await deleteUserProduct(id);
    return id;
  }
);

const userProductsSlice = createSlice({
  name: 'userProducts',
  initialState,
  reducers: {
    selectItem(state: UserProductsState, action: PayloadAction<string>) {
      const found = state.items.find(item => item.id === action.payload);
      state.selectedItem = found ? found : null;
    },
    clearSelectedItem(state: UserProductsState) {
      state.selectedItem = null;
    },
    updateItem(state: UserProductsState, action: PayloadAction<CacheListItemData>) { 
      const index = state.items.findIndex(item => item.id === action.payload.id);
      if (index !== -1) {
        // Use direct assignment - Immer handles immutability
        state.items[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all user products
      .addCase(fetchUserProducts.pending, (state) => {
        state.loading = 'pending';
        state.error = null;
      })
      .addCase(fetchUserProducts.fulfilled, (state, action) => {
        state.loading = 'succeeded';
        state.items = action.payload;
        state.fetchedForUserId = action.meta.arg;
      })
      .addCase(fetchUserProducts.rejected, (state, action) => {
        state.loading = 'failed';
        state.fetchedForUserId = action.meta.arg;
        state.error = action.error.message || 'Failed to fetch user products';
      })
      
      // Fetch user product by ID
      .addCase(fetchUserProductById.pending, (state) => {
        state.loading = 'pending';
        state.error = null;
      })
      .addCase(fetchUserProductById.fulfilled, (state, action) => {
        state.loading = 'succeeded';
        state.selectedItem = action.payload;
        
        // Update the item in the items array if it exists
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        } else {
          // Only add if it wasn't already there (might happen on direct fetch)
          state.items.push(action.payload);
        }
      })
      .addCase(fetchUserProductById.rejected, (state, action) => {
        state.loading = 'failed';
        state.error = action.error.message || 'Failed to fetch user product';
      })
      
      // Add new user product
      .addCase(addNewUserProduct.pending, (state) => {
        state.loading = 'pending';
        state.error = null;
      })
      .addCase(addNewUserProduct.fulfilled, (state, action) => {
        state.loading = 'succeeded';
        if (action.payload) {
          state.items.unshift(action.payload);
          state.selectedItem = action.payload; // Set selected item to the newly added one
        }
      })
      .addCase(addNewUserProduct.rejected, (state, action) => {
        state.loading = 'failed';
        state.error = action.error.message || 'Failed to add user product';
      })
      
      // Update user product
      .addCase(updateExistingUserProduct.pending, (state) => {
        state.loading = 'pending';
        state.error = null;
      })
      .addCase(updateExistingUserProduct.fulfilled, (state, action) => {
        state.loading = 'succeeded';
        if (action.payload) {
          const index = state.items.findIndex(item => item.id === action.payload?.id);
          if (index !== -1) {
            state.items[index] = action.payload;
          }
          if (state.selectedItem?.id === action.payload.id) {
            state.selectedItem = action.payload;
          }
        }
      })
      .addCase(updateExistingUserProduct.rejected, (state, action) => {
        state.loading = 'failed';
        state.error = action.error.message || 'Failed to update user product';
      })
      
      // Delete user product
      .addCase(removeUserProduct.pending, (state) => {
        state.loading = 'pending';
        state.error = null;
      })
      .addCase(removeUserProduct.fulfilled, (state, action) => {
        state.loading = 'succeeded';
        state.items = state.items.filter(item => item.id !== action.payload);
        if (state.selectedItem?.id === action.payload) {
          state.selectedItem = null;
        }
      })
      .addCase(removeUserProduct.rejected, (state, action) => {
        state.loading = 'failed';
        state.error = action.error.message || 'Failed to delete user product';
      });
  },
});

export const { selectItem, clearSelectedItem, updateItem } = userProductsSlice.actions;
export default userProductsSlice.reducer;
